import { createDataMapper } from '@common/utils/query/dataMappers';
import { deepClone } from '@common/utils/deepClone';
import { mergeWithDefault } from '@common/utils/mergeWithDefault';
import { isSupportedLocaleCode } from '@constants/locales';
import { FetchBaseQueryMeta } from '@reduxjs/toolkit/query';
import { getLocalizedObject, getLocalizedValue } from './localizedMetadata';
import {
    BackendCollectionMetadata,
    CollectionMetadata,
    Credits,
    DEFAULT_COLLECTION_METADATA,
    DEFAULT_CREDITS,
    DEFAULT_IMAGES,
    DEFAULT_LOCALIZED_GENRES,
    DEFAULT_LOCALIZED_METADATA,
    DEFAULT_LOCALIZED_SYNOPSES,
    DEFAULT_LOCALIZED_TITLES,
    DEFAULT_SYNOPSIS,
    DEFAULT_TITLE,
    GetCollectionMetadataArgs,
    Images,
    isCreditRole,
    isImageType,
    isSynopsisType,
    isTitleType,
    LocalizedGenres,
    LocalizedSynopses,
    LocalizedTitles,
} from './metadataTypes';

const transformTitles = (input: BackendCollectionMetadata): LocalizedTitles => {
    const rawTitles: Partial<LocalizedTitles> = input.titles?.reduce(
        (acc, { type, lang, value }) => {
            if (isSupportedLocaleCode(lang) && isTitleType(type)) {
                if (!acc[lang]) {
                    acc[lang] = deepClone(DEFAULT_TITLE);
                }
                acc[lang][type] = value;
            }
            return acc;
        },
        {} as Partial<LocalizedTitles>,
    );

    return mergeWithDefault(rawTitles, DEFAULT_LOCALIZED_TITLES);
};

const transformCredits = (input: BackendCollectionMetadata): Credits => {
    const rawCredits: Partial<Credits> = input.credits?.reduce(
        (acc, { name, role }) => {
            if (isCreditRole(role)) {
                if (!acc[role]) {
                    acc[role] = deepClone(DEFAULT_CREDITS[role]);
                }
                acc[role].push(name);
            }
            return acc;
        },
        {} as Partial<Credits>,
    );

    return mergeWithDefault(rawCredits, DEFAULT_CREDITS);
};

const transformGenres = (input: BackendCollectionMetadata): LocalizedGenres => {
    const rawGenresMap: Partial<LocalizedGenres> = {};
    const { lang, values } = input.genres ?? {};

    if (isSupportedLocaleCode(lang)) {
        rawGenresMap[lang] = values;
    }

    return mergeWithDefault(rawGenresMap, DEFAULT_LOCALIZED_GENRES);
};

const transformSynopses = (
    input: BackendCollectionMetadata,
): LocalizedSynopses => {
    const rawSynopsesMap: Partial<LocalizedSynopses> = input.synopses?.reduce(
        (acc, { lang, type, value }) => {
            if (isSupportedLocaleCode(lang) && isSynopsisType(type)) {
                if (!acc[lang]) {
                    acc[lang] = deepClone(DEFAULT_SYNOPSIS);
                }
                acc[lang][type] = value;
            }
            return acc;
        },
        {} as Partial<LocalizedSynopses>,
    );

    return mergeWithDefault(rawSynopsesMap, DEFAULT_LOCALIZED_SYNOPSES);
};

const transformImages = (input: BackendCollectionMetadata): Images => {
    const rawImagesMap: Partial<Images> = input.images?.reduce(
        (acc, { id, size, url }) => {
            const type = id.slice(id.lastIndexOf('/') + 1);
            if (isImageType(type)) {
                acc[type] = { size, url };
            }
            return acc;
        },
        {} as Partial<Images>,
    );

    return mergeWithDefault(rawImagesMap, DEFAULT_IMAGES);
};

const transformTrailers = (input: BackendCollectionMetadata): string[] => {
    const params = input.trailerUrl?.start?.params;

    if (!params) {
        return DEFAULT_COLLECTION_METADATA.trailers;
    }

    return params.flatMap(({ value }) => value.args.map(({ path }) => path));
};

const transformDuration = (input: BackendCollectionMetadata): string => {
    const duration = input.duration;

    if (!duration) {
        return DEFAULT_COLLECTION_METADATA.duration;
    }

    return duration
        .split(/PT(?:([.,\d]+)H)?(?:([.,\d]+)M)?(?:([.,\d]+)S)?/)
        .filter(Boolean)
        .join(':');
};

const mapBackendCollectionMetadataToFrontendCollectionMetadata = (
    backendMetadata: BackendCollectionMetadata,
    _meta: FetchBaseQueryMeta | undefined,
    arg: GetCollectionMetadataArgs,
): CollectionMetadata => {
    const { proscopeId, title: encodedTitle, collectionId, locale } = arg;

    const titles = transformTitles(backendMetadata);
    const credits = transformCredits(backendMetadata);
    const genres = transformGenres(backendMetadata);
    const synopses = transformSynopses(backendMetadata);
    const images = transformImages(backendMetadata);
    const trailers = transformTrailers(backendMetadata);
    const duration = transformDuration(backendMetadata);

    const localizedTitle =
        getLocalizedValue(titles, 'Principal', locale) || encodedTitle;
    const localizedGenres = getLocalizedObject(genres, locale);
    const LocalizedSynopses = getLocalizedObject(synopses, locale);

    const rawResult: Partial<CollectionMetadata> = {
        proscopeId,
        collectionId,

        releaseDate: backendMetadata.releaseDate,
        productionCountry: backendMetadata.productionCountry,

        duration,
        credits,
        images,
        trailers,

        title: localizedTitle,
        genres: localizedGenres,
        synopses: LocalizedSynopses,
    };

    return mergeWithDefault(rawResult, DEFAULT_LOCALIZED_METADATA);
};

export const transformCollectionMetadata = createDataMapper<
    BackendCollectionMetadata,
    CollectionMetadata,
    GetCollectionMetadataArgs
>(mapBackendCollectionMetadataToFrontendCollectionMetadata);
